import orderBy from 'lodash/orderBy'
import type {Contract, FullContract} from '@/models/contract'
import type {Customer, Property} from '@/models/customers'
import {CONTRACT_COMPLETIONS, CONTRACT_PERIODS, CONTRACT_STATES, STATUS_SBL} from '@/constants/contract'
import {useCustomerService} from "~/services/customerService";
import {
  CONTRACT_TYPE_FOREST_OPERATION,
  CONTRACT_TYPE_SILVICULTURE_AGREEMENT,
  CONTRACT_TYPE_SILVICULTURE_ORDER, CONTRACT_TYPE_SUPPLY_WOOD
} from "~/constants/contracts";

type ContractTypeWithYearAndMonth = Pick<Contract, 'Year' | 'LoggingMonth' | 'Status' | 'Id' | 'ForestOwnerName'>

export function getContractsByDate <T extends ContractTypeWithYearAndMonth> (contracts: T[]): { [key: string]: { [month: number]: T[] } } {
  return orderBy(contracts, ['Status', 'ForestOwnerName']).reduce((acc, contract) => ({
    ...acc,
    [contract.Year || '']: {
      ...acc[contract.Year!],
      [contract.LoggingMonth || '']: [
        ...((acc[contract.Year!] && acc[contract.Year!][contract.LoggingMonth || '']) || []),
        contract
      ]
    }
  }), {} as { [key: string]: { [month: number]: T[] } })
}

export function getContractBaseUrl (contract: Contract | FullContract): string {
  return `/customers/${contract.ForestOwnerId}/${contract.PropertyId}/${contract.Id}`
}

export const getContractCustomer = async (contract: Contract | FullContract): Promise<Customer | undefined | null> => {
  if (!contract.ForestOwnerId) {
    return {}
  }
  try {
    const {getCustomerById} = useCustomerService()
    return await getCustomerById(contract.ForestOwnerId)
  } catch (error) {
    console.error(error)
  }
}

export async function getContractProperty(contract: Contract): Promise<Property | undefined> {
  try {
    const customer = await getContractCustomer(contract)
    return customer && contract && contract.PropertyId ? customer.Properties?.find(p => p.Id === contract.PropertyId) : undefined
  } catch (error) {
    console.error(error)
  }
}

export const getIsFullContract = (contract?: Contract | FullContract): boolean => {
    return !!contract && (contract as FullContract).AssortmentInformation !== undefined
}

export function getContractStateLabel (contract: Contract): string {
  return CONTRACT_STATES.find(s => s.Id === contract.Status) ? CONTRACT_STATES.find(s => s.Id === contract.Status)?.Name || '' : ''
}

export function getContractStatusSblLabel (contract: Contract): string {
  return STATUS_SBL.find(s => s.Id === contract.StatusSbl) ? STATUS_SBL.find(s => s.Id === contract.StatusSbl)?.Name || '' : ''
}

export function getContractPeriodLabel (contract: FullContract): string {
  return CONTRACT_PERIODS.find(s => s.Id === contract.Period) ? CONTRACT_PERIODS.find(s => s.Id === contract.Period)?.Name || '' : ''
}

export function getContractCompletionLabel (contract: FullContract): string {
  return CONTRACT_COMPLETIONS.find(s => s.Id === contract.AssortmentInformation?.CompleteOperationTarget) ? CONTRACT_COMPLETIONS.find(s => s.Id === contract.AssortmentInformation?.CompleteOperationTarget)?.Name || '' : ''
}

export const calculateTopplePlaceVolume = (contract: FullContract): number => {
  if (contract.AssortmentInformation && contract.AssortmentInformation.TotalWoodForSale) {
    let volume = contract.AssortmentInformation.TotalWoodForSale
    contract.TopplePlaces?.forEach((place) => {
      if (place.Capacity) {
        volume -= place.Capacity
      }
    })
    return volume
  } else {
    return 0
  }
}

export function isTempContract(id: string): boolean {
  return /^temp_.+/.test(id.toString())
}

export const getContractHasSilviculture = (contract: FullContract): boolean => {
  if(contract.Type === CONTRACT_TYPE_SILVICULTURE_AGREEMENT) {
    return true;
  } else if(
      contract.Type === CONTRACT_TYPE_FOREST_OPERATION ||
      contract.Type === CONTRACT_TYPE_SUPPLY_WOOD
  ) {
    const silviCultureActionPlans = contract.AllmaJobs
        ?.flatMap((job) => job.Actions || [])
        .flatMap((action) => action.SilviCultureActionPlans || [])
    return !!silviCultureActionPlans && silviCultureActionPlans.length > 0;
  }
  else {
    return false;
  }
}
